import React, { useState, useEffect, useCallback } from "react";
import { listVentasCocina, updateVentaCocina, listDetalleMesas } from "../api/ventasCocina";
import { listVentasGeneral, updateventaGeneral, listTiposVenta } from "../api/ventasGeneral";
import { listClientLazy } from "../api/clientesReceptor";
import { 
  FiCoffee, FiClock, FiCheckCircle, FiAlertCircle, 
  FiRefreshCw, FiChevronRight, FiUsers, FiTruck
} from "react-icons/fi";
import { showAlertError, showAlertExito } from "../utils/alertHelper";
import Loader from "./Loader";

const Cocina = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [tiposVenta, setTiposVenta] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Importar listDetalleMesas desde la API
  const fetchOrdenes = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    
    try {
      // Traer órdenes (mesas + general), mesas y tipos
      const [resCocina, resGeneral, mesasDataRaw, tiposDataRaw, resClientes] = await Promise.all([
        listVentasCocina(),
        listVentasGeneral(),
        listDetalleMesas(),
        listTiposVenta(),
        listClientLazy(1, 100, "")
      ]);

      const mesasData = Array.isArray(mesasDataRaw) ? mesasDataRaw : (mesasDataRaw?.data || []);
      setMesas(mesasData);

      const tiposData = Array.isArray(tiposDataRaw) ? tiposDataRaw : (tiposDataRaw?.data || []);
      setTiposVenta(tiposData);
      
      setClientes(resClientes?.data || []);

      const normalizeRes = (res) => {
        if (Array.isArray(res)) return res;
        if (res?.data && Array.isArray(res.data)) return res.data;
        if (res?.ventas && Array.isArray(res.ventas)) return res.ventas;
        return [];
      };

      const activasCocina = normalizeRes(resCocina).filter(o => Number(o.status) !== 3);
      const activasGeneral = normalizeRes(resGeneral).filter(o => Number(o.status) !== 3);

      // Combinar y ordenar por fecha de creación (más recientes primero o más antiguas?)
      // Usualmente en cocina se quieren las más antiguas primero para FIFO
      const todas = [...activasCocina, ...activasGeneral].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      
      setOrdenes(todas);
    } catch (error) {
      console.error("Error fetching kitchen orders:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrdenes(true);
    // Polling cada 30 segundos
    const interval = setInterval(() => fetchOrdenes(), 30000);
    return () => clearInterval(interval);
  }, [fetchOrdenes]);

  const handleUpdateStatus = async (orden, newStatus) => {
    try {
      setRefreshing(true);
      // Al marcar todo como servido (2), también actualizamos todos los ítems internos
      const dataRaw = orden.json;
      const parsed = typeof dataRaw === 'string' ? JSON.parse(dataRaw) : (dataRaw || {});
      let items = Array.isArray(parsed) ? parsed : (parsed.items || []);
      
      if (Number(newStatus) === 2) {
        items = items.map(it => ({ ...it, estado: 2 }));
      }
      
      const newJson = JSON.stringify(Array.isArray(parsed) ? items : { ...parsed, items });

      const isGeneral = !!orden.id_venta_tipo;
      const updateFn = isGeneral ? updateventaGeneral : updateVentaCocina;

      const payload = {
        json: newJson,
        status: newStatus,
        // Campos para mesas
        id_mesa: orden.id_mesa,
        cantidad_personas: orden.cantidad_personas || 1,
        numero_mesa: orden.numero_mesa,
        id_sucursal: orden.id_sucursal,
        // Campos para general
        id_venta_tipo: orden.id_venta_tipo
      };
      
      await updateFn(orden.id, payload);
      await fetchOrdenes();
      const identifier = isGeneral ? (tiposVenta.find(t => t.id === orden.id_venta_tipo)?.tipo || "Venta Gral") : `Mesa ${orden.numero_mesa}`;
      showAlertExito("¡Estado Actualizado!", `${identifier} marcada como ${newStatus === 2 ? 'Servida' : 'En Proceso'}`);
    } catch (error) {
      console.error("Error updating status:", error);
      showAlertError("Error", "No se pudo actualizar el estado de la orden.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleServeItem = async (orden, itemIndex) => {
    try {
      setRefreshing(true);
      const parsed = typeof orden.json === 'string' ? JSON.parse(orden.json) : (orden.json || {});
      const items = Array.isArray(parsed) ? parsed : (parsed.items || []);
      
      // Actualizar solo el ítem seleccionado
      if (items[itemIndex]) {
        items[itemIndex].estado = 2; // Servido
      }
      
      const newJson = JSON.stringify(Array.isArray(parsed) ? items : { ...parsed, items });
      
      const isGeneral = !!orden.id_venta_tipo;
      const updateFn = isGeneral ? updateventaGeneral : updateVentaCocina;

      const payload = {
        json: newJson,
        status: orden.status,
        id_mesa: orden.id_mesa,
        id_venta_tipo: orden.id_venta_tipo,
        cantidad_personas: orden.cantidad_personas || 1,
        numero_mesa: orden.numero_mesa,
        id_sucursal: orden.id_sucursal
      };
      
      await updateFn(orden.id, payload);
      await fetchOrdenes();
      showAlertExito("Ítem actualizado", "Se marcó el producto como servido.");
    } catch (error) {
      console.error("Error serving item:", error);
      showAlertError("Error", "No se pudo actualizar el ítem.");
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (Number(status)) {
      case 1: return { label: "Pendiente", color: "bg-orange-100 text-orange-600 border-orange-200" };
      case 2: return { label: "Servido", color: "bg-emerald-100 text-emerald-600 border-emerald-200" };
      default: return { label: "Desconocido", color: "bg-slate-100 text-slate-600" };
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 p-6 flex items-center justify-between shadow-sm shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-100">
            <FiCoffee size={24} />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight">Monitor de Cocina</h2>
            <p className="text-sm text-slate-400 font-bold uppercase tracking-widest flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              {ordenes.length} Órdenes en Curso
            </p>
          </div>
        </div>
        <button 
          onClick={() => fetchOrdenes()}
          disabled={refreshing}
          className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 active:scale-95 shadow-sm
            ${refreshing ? 'opacity-50' : ''}`}
        >
          <FiRefreshCw className={refreshing ? 'animate-spin' : ''} /> 
          {refreshing ? 'Actualizando...' : 'Refrescar'}
        </button>
      </header>

      {/* BODY - KITCHEN BOARD */}
      <main className="flex-1 overflow-y-auto p-6 scrollbar-hide">
        {ordenes.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
            <div className="w-24 h-24 bg-white rounded-[2.5rem] shadow-xl flex items-center justify-center mb-6">
              <FiCoffee size={40} className="text-slate-200" />
            </div>
            <h3 className="text-xl font-black text-slate-800">Cocina Despejada</h3>
            <p className="text-slate-400 font-medium mt-2">No hay pedidos pendientes en este momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8 animate-fade-in">
            {ordenes.map((orden) => {
              const status = getStatusInfo(orden.status);
              
              // Buscar el número de mesa real cruzando con el estado de mesas
              const mesaInfo = mesas.find(m => m.id === orden.id_mesa);
              const numeroMesaActual = mesaInfo ? mesaInfo.numero_mesa : (orden.numero_mesa || '?');

              let orderData = { items: [] };
              try {
                const dataRaw = orden.json;
                const parsed = typeof dataRaw === 'string' ? JSON.parse(dataRaw) : (dataRaw || {});
                
                if (Array.isArray(parsed)) {
                   orderData.items = parsed;
                } else if (parsed.items && Array.isArray(parsed.items)) {
                   orderData.items = parsed.items;
                } else {
                   orderData.items = []; // No se encontraron items
                }
              } catch (e) {
                console.error("Error parsing order JSON", e);
              }

              const items = orderData.items;
              const fecha = new Date(orden.createdAt);
              const tiempo = Math.floor((new Date() - fecha) / 60000); // minutos

              return (
                <div 
                  key={orden.id} 
                  className="bg-white rounded-[2rem] border-2 border-slate-100 shadow-sm flex flex-col overflow-hidden hover:border-indigo-100 hover:shadow-xl transition-all duration-300"
                >
                  {/* Card Header */}
                  <div className={`p-5 flex items-center justify-between border-b border-slate-50 ${orden.status === 1 ? 'bg-orange-50/30' : 'bg-emerald-50/30'}`}>
                    <div className="flex items-center gap-3">
                       {orden.id_mesa ? (
                         <span className="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center font-black italic">
                           M{numeroMesaActual}
                         </span>
                       ) : (
                         <div className="w-10 h-10 rounded-xl bg-indigo-50 overflow-hidden flex items-center justify-center border border-indigo-100">
                           {(() => {
                              const tipo = tiposVenta.find(t => t.id === orden.id_venta_tipo);
                              return tipo?.logo ? (
                                <img src={`${process.env.REACT_APP_RUTA_PRODUCTOS}${tipo.logo}`} className="w-full h-full object-contain p-1" alt={tipo.tipo} />
                              ) : <FiTruck className="text-indigo-400" size={20} />;
                           })()}
                         </div>
                       )}
                       <div>
                         <h4 className="text-xs font-black text-slate-800 leading-none">
                           {orden.id_mesa ? `Mesa ${numeroMesaActual}` : (tiposVenta.find(t => t.id === orden.id_venta_tipo)?.tipo || "Venta Gral")}
                         </h4>
                         {(() => {
                            try {
                              const data = JSON.parse(orden.json);
                              if (data.idCliente) {
                                const c = clientes.find(cl => cl.id === data.idCliente);
                                if (c) return <p className="text-[10px] text-slate-400 font-bold truncate max-w-[120px]">{c.nombre}</p>;
                              }
                            } catch(e) {}
                            return null;
                         })()}
                         <span className={`text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-full border mt-1 inline-block ${status.color}`}>
                           {status.label}
                         </span>
                       </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Pedido hace</span>
                      <span className={`text-xs font-black flex items-center gap-1 ${tiempo > 15 ? 'text-rose-500 animate-pulse' : 'text-slate-500'}`}>
                        <FiClock /> {tiempo} min
                      </span>
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="flex-1 p-6 space-y-4 overflow-y-auto max-h-[300px] scrollbar-hide">
                    {items.length === 0 && <p className="text-xs text-slate-400 italic">Sin productos registrados</p>}
                    {items.map((it, idx) => (
                      <div key={idx} className="flex items-start justify-between group py-2 border-b border-slate-50 last:border-0">
                        <div className="flex gap-4">
                          <span className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-sm font-black text-indigo-600">
                            {it.cantidad}
                          </span>
                          <div>
                            <p className={`font-bold text-slate-800 leading-tight ${Number(it.estado) === 2 ? 'line-through opacity-30' : ''}`}>
                              {it.name || 'Producto'}
                            </p>
                            {it.note && (
                              <p className="text-[11px] text-amber-600 font-black mt-1 bg-amber-50 px-2 py-0.5 rounded-lg border border-amber-100 italic animate-pulse">
                                <FiAlertCircle className="inline mr-1" size={12} />
                                {it.note}
                              </p>
                            )}
                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-md border mt-1 inline-block
                              ${Number(it.estado) === 2 ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-orange-50 text-orange-600 border-orange-100'}`}>
                              {Number(it.estado) === 2 ? 'Servido' : 'Pendiente'}
                            </span>
                          </div>
                        </div>
                        
                        {Number(it.estado) !== 2 && (
                          <button 
                            onClick={() => handleServeItem(orden, idx)}
                            className="w-10 h-10 rounded-2xl bg-white border border-slate-200 text-slate-400 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-all flex items-center justify-center shadow-sm"
                            title="Despachar Plato"
                          >
                            <FiCheckCircle size={20} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Footer Actions */}
                  <div className="p-5 bg-slate-50/50 mt-auto border-t border-slate-50">
                    {orden.status === 1 ? (
                      <button 
                        onClick={() => handleUpdateStatus(orden, 2)}
                        className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-3"
                      >
                        <FiCheckCircle size={18} /> Marcar como Servido
                      </button>
                    ) : (
                      <div className="flex items-center justify-center gap-3 py-4 text-emerald-600 font-black text-xs uppercase tracking-widest">
                        <FiCheckCircle size={20} /> Pedido Servido
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Cocina;
